import { useEffect, useState } from 'react';
import { useManagedMutation } from 'utils/managedHooks';
import { ADD_ADDITIONAL_DETAILS, ADD_NOTE_TO_BOOKING, CREATE_PROVISIONAL, OVERRIDE_PRICE } from 'store/mutations';

import { calculateMargins } from 'utils/trainMargins';

const addDetailsForMiceGroupBooking = async ({ data, isMiceGroupBooking, addAdditionalDetails, addNote }) => {
  if (isMiceGroupBooking) {
    const { createBooking } = data;
    const { reference } = createBooking;

    await addAdditionalDetails({
      variables: {
        bookingReference: reference,
        additionalDetails: [
          {
            key: 'MICE_GROUP',
            value: 'yes'
          }
        ]
      }
    });
    await addNote({
      variables: {
        bookingReference: reference,
        note: 'MICE Group Booking'
      }
    });
  }
};

async function applyTrainMargins({ data, isPackageBooking = false, overridePrice }) {
  const { createBooking } = data;
  const { reference } = createBooking;

  const items = calculateMargins({ ...createBooking, isPackageBooking });

  if (!items.length) {
    return;
  }

  await overridePrice({
    variables: {
      reference,
      items
    }
  });
}

const useCreateProvisionalBooking = ({ update = () => {}, isMiceGroupBooking = false } = {}) => {
  const [createProvisional, { loading: loadingCreateProvisional, error: errorCreateProvisional }] = useManagedMutation(CREATE_PROVISIONAL, {
    update
  });
  const [overridePrice, { loading: loadingOverridePrice, error: errorOverridePrice }] = useManagedMutation(OVERRIDE_PRICE);
  const [addAdditionalDetails, { loading: loadingAdditionalDetails, error: errorAdditionalDetails }] =
    useManagedMutation(ADD_ADDITIONAL_DETAILS);
  const [addNote, { loading: loadingNote, error: errorNote }] = useManagedMutation(ADD_NOTE_TO_BOOKING);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(undefined);

  useEffect(() => {
    const currentlyLoading = loadingCreateProvisional || loadingOverridePrice || loadingAdditionalDetails || loadingNote;
    if (loading !== currentlyLoading) {
      setLoading(currentlyLoading);
    }
  }, [loadingCreateProvisional, loadingOverridePrice, loadingAdditionalDetails, loadingNote, loading, setLoading]);

  useEffect(() => {
    const currentError = errorCreateProvisional ?? errorOverridePrice ?? errorAdditionalDetails ?? errorNote;
    if (error !== currentError) {
      setError(currentError);
    }
  }, [errorCreateProvisional, errorOverridePrice, errorAdditionalDetails, errorNote, error, setError]);

  const handleMutation = async ({ variables, isPackageBooking, onCompleted }) => {
    const { data } = await createProvisional({ variables });
    await applyTrainMargins({ data, isPackageBooking, overridePrice });
    await addDetailsForMiceGroupBooking({ data, isMiceGroupBooking, addAdditionalDetails, addNote });
    return onCompleted ? await onCompleted(data) : data;
  };

  return [handleMutation, { loading, error }];
};

export default useCreateProvisionalBooking;
